import {
  useState, useEffect, useCallback,
} from 'react'
import {
  useSearchParams,
} from 'react-router-dom'
import {
  Mail, Loader2, Check, Copy, ShieldCheck,
} from 'lucide-react'
import {
  assistantEmailService,
} from '../services/api'
import {
  DNS_VERIFY_POLL_MAX_MS,
  DNS_VERIFY_POLL_MIN_MS,
  usePollWithBackoff,
} from '../hooks/usePollWithBackoff'
import type {
  DnsInstructionsResponse,
} from '../services/api'

/**
 * Public, token-authenticated page the user forwards to their IT team. It shows
 * the NS records to delegate the `catch.<company>` subdomain to Catch and a
 * button to verify the connection — no Catch login required.
 */
export function ConnectEmailPage(): JSX.Element {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [instructions, setInstructions] = useState<DnsInstructionsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [verifying, setVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)

  const load = useCallback(async () => {
    if (!token) {
      setError('This setup link is invalid or has expired.')
      setLoading(false)
      return
    }
    try {
      setInstructions(await assistantEmailService.getDnsInstructions(token))
    } catch {
      setError('This setup link is invalid or has expired.')
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    void load()
  }, [load])

  const verify = useCallback(async () => {
    if (!token) return
    setVerifying(true)
    try {
      const result = await assistantEmailService.verifySubdomain(token)
      setInstructions((prev) =>
        prev ? { ...prev, status: result.status, verified: result.verified } : prev,
      )
    } catch {
      // ignore transient verification errors
    } finally {
      setVerifying(false)
    }
  }, [token])

  // Poll until verified, backing off as we go. This is the surface where the
  // endpoint's per-IP cap actually bites: it's opened by the customer's IT
  // team, from a corporate egress IP, quite possibly on several machines, on a
  // page whose whole purpose is to sit open while somebody edits DNS. At a flat
  // 10s that is ~6 requests/minute PER TAB against a 60/minute per-IP cap, so
  // roughly ten open tabs behind one office NAT trips it — and `verify`
  // swallows the error, so IT would just see "not verified" forever.
  usePollWithBackoff({
    enabled: Boolean(instructions) && !instructions?.verified,
    onPoll: verify,
    minMs: DNS_VERIFY_POLL_MIN_MS,
    maxMs: DNS_VERIFY_POLL_MAX_MS,
  })

  const copy = async (value: string, index: number) => {
    await navigator.clipboard.writeText(value)
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 2000)
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl border border-gray-200 p-8">
        <div className="flex items-center gap-3 mb-2">
          <Mail className="w-6 h-6 text-primary-500" />
          <h1 className="text-xl font-semibold text-gray-900">
            Connect your company email to Catch
          </h1>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600 py-6">{error}</p>
        ) : instructions ? (
          instructions.verified ? (
            <div className="py-6 text-center">
              <ShieldCheck className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-lg font-medium text-gray-900">All set!</p>
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-mono">{instructions.subdomain}</span> is verified
                and ready. You can close this page.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-5">
                Add the following <strong>NS records</strong> for the subdomain{' '}
                <span className="font-mono">{instructions.subdomain}</span> in the DNS
                settings for <span className="font-mono">{instructions.parent_domain}</span>.
                This delegates only the <span className="font-mono">catch</span>{' '}
                subdomain to Catch — the rest of your domain is untouched.
              </p>

              <ul className="space-y-2 mb-6">
                {instructions.nameservers.map((ns, index) => (
                  <li
                    key={ns}
                    className="flex items-center justify-between gap-3 font-mono text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                  >
                    <span className="truncate text-gray-800">{ns}</span>
                    <button
                      type="button"
                      onClick={() => void copy(ns, index)}
                      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
                    >
                      {copiedIndex === index ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                      {copiedIndex === index ? 'Copied' : 'Copy'}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => void verify()}
                  disabled={verifying}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
                >
                  {verifying ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-4 h-4" />
                  )}
                  Verify connection
                </button>
                <span className="text-xs text-gray-500">
                  DNS changes can take up to a few hours to propagate. We’ll keep
                  checking automatically.
                </span>
              </div>
            </>
          )
        ) : null}
      </div>
    </div>
  )
}
